import {debounce, flowRight as compose, get, has} from 'lodash';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {useQuery} from 'react-apollo';
import {Container} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import 'react-placeholder/lib/reactPlaceholder.css';
import {ORDER_DIRECTION} from '../../constants';
import Pagination from './Pagination';
import Search from './Search';
import Select from './Select';
import DatatableCore from './DatatableCore';
import {Flex} from './DatatableStyles';


const Datatable = ({columns, t, keyResult, query, onSearch, defaultSort, advancedFilters, filters, additionalFilters, customQuery, fetchPolicy}) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [limit, setLimit] = useState(10);
  const [order, setOrder] = useState(null);
  const [forceLoading, setForceLoading] = useState(false);
  const setOrderField = (field, sortKey) => {
    let direction = !order || (order && (order.direction === ORDER_DIRECTION.DESC || (![field, sortKey].includes(order.field))))
      ? ORDER_DIRECTION.ASC
      : ORDER_DIRECTION.DESC;

    setOrder({field: sortKey || field, direction});
  };
  const orders = order ? [order] : [];
  if (defaultSort) {
    orders.push(defaultSort);
  }

  useEffect(() => {
    setPageIndex(0);
  }, [advancedFilters, filters]);

  useEffect(() => {
    setForceLoading(true);
    setTimeout(() => {
      setForceLoading(false);
    }, 500);
  }, [advancedFilters, filters, limit, order]);

  let variables = {
    criteria: {
      orders,
      limit,
      offset: pageIndex * limit,
      filters,
      advancedFilters,
    },
  };

  if (customQuery) {
    variables = {
      customQuery,
      ...variables,
    };
  }

  const {loading, error, data} = useQuery(query, {variables, fetchPolicy});
  const result = has(data, keyResult) ? get(data, keyResult) : {};
  const pageCount = Math.ceil(result.total / limit);

  const onLimitChange = event => {
    setPageIndex(0);
    setLimit(+event.target.value);
  };

  const onSearchChange = debounce(value => {
    setPageIndex(0);
    onSearch(value);
  }, 500);

  return (
    <Container fluid>
      <Flex>
        <Select value={limit} onChange={onLimitChange}/>
        <div className="filter">
          {additionalFilters}
          <Search onCancel={() => onSearchChange('')} onChange={event => onSearchChange(event.target.value)}/>
        </div>
      </Flex>
      <br/>
      <DatatableCore
        columns={columns}
        onClickSortable={setOrderField}
        loading={loading || forceLoading}
        error={error}
        result={result}
        limit={limit}
      />
      <Pagination pageCount={pageCount} pageIndex={pageIndex} setPageIndex={setPageIndex} total={result.total || 0}/>
    </Container>
  );
};

Datatable.propTypes = {
  columns: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  keyResult: PropTypes.string.isRequired,
  query: PropTypes.object.isRequired,
  customQuery: PropTypes.string,
  defaultSort: PropTypes.shape({
    field: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(Object.values(ORDER_DIRECTION)),
  }),
  filters: PropTypes.object,
  advancedFilters: PropTypes.object,
  additionalFilters: PropTypes.element,
  fetchPolicy: PropTypes.string,
};

Datatable.defaultProps = {
  fetchPolicy: 'cache-first',
};

export default compose(
  withTranslation('common'),
)(Datatable);
